import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError, firstValueFrom } from 'rxjs';
import { catchError, finalize, shareReplay, tap } from 'rxjs/operators';
import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
  platformAuthenticatorIsAvailable,
  WebAuthnAbortService,
} from '@simplewebauthn/browser';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import { environment } from '../../environments/environment';
import { User, AuthResponse, AuthCredentials, RegisterData, JwtPayload } from '../models/user.model';
import { ThemeService } from './theme.service';

/** One of the user's registered passkeys, as shown in account settings. */
export interface PasskeySummary {
  id: string;
  /** SCRUM-1974 — base64url WebAuthn credential id, used for the browser's
   *  credential-hygiene signal on revoke (the `id` above is the DB row key). */
  credentialId: string;
  nickname: string | null;
  transports: string[];
  createdAt: string;
  lastUsedAt: string | null;
}

/** A trusted device (long-lived, idle-exempt session) for the account-settings
 *  management list — SCRUM-2019. */
export interface TrustedDeviceSummary {
  id: string;
  label: string | null;
  created_at: string;
  last_seen_at: string;
}

/**
 * JWT decoding utility
 */
function decodeToken(token: string): JwtPayload | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    return JSON.parse(atob(payload));
  } catch (e) { // NOSONAR S2486 — intentional swallow for best-effort operation
    return null;
  }
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly API_URL = environment.api.auth;
  private readonly TOKEN_KEY = 'hub_access_token';
  private readonly REFRESH_TOKEN_KEY = 'hub_refresh_token';
  private readonly EXPIRES_IN_KEY = 'hub_token_expires_in';
  private readonly USER_KEY = 'hub_user';
  private readonly FIRST_NAME_KEY = 'hub_first_name';
  private readonly DISPLAY_NAME_KEY = 'hub_display_name';
  private readonly ROLE_KEY = 'hub_role';

  // 30-minute inactivity logout (Honman 2026-07-17, updated from the prior
  // 15-min prod / 4-hr dev split — one consistent window across environments
  // so behavior is testable on dev without waiting 4 hours). DOM events
  // reset the timer; firing it logs the user out and bounces them to the
  // login page with reason=inactivity so the banner explains why.
  private inactivityTimer: any = null;
  private readonly INACTIVITY_TIMEOUT = 30 * 60 * 1000;
  private readonly ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
  private readonly activityHandler = () => this.resetInactivityTimer();

  // Proactive session-expiry watchdog (2026-07-24). The inactivity timer above
  // logs the user out after 30 min idle, but a setTimeout is throttled or fully
  // suspended in a backgrounded tab and across a laptop sleep — so a token that
  // expires while the user is away would leave them on a stale authenticated
  // page until their next request 401s. This watchdog re-checks the token's
  // real expiry timestamp whenever the tab regains focus/visibility and on a
  // short interval, and bounces a timed-out session to the login page.
  private sessionWatchdog: any = null;
  private readonly SESSION_CHECK_INTERVAL = 60 * 1000;
  private readonly visibilityHandler = () => {
    if (document.visibilityState === 'visible') this.enforceSessionValidity();
  };

  // 2026-06-23 — concurrent-refresh race fix. When the dashboard fires many
  // requests in parallel and they all 401 at once (token expired mid-burst,
  // or auth-service restart invalidated the in-memory rotation chain), each
  // 401 used to independently call refreshToken(). The first call succeeded
  // and rotated the HttpOnly hub_refresh cookie; the rest used the now-stale
  // cookie, got 401 from /auth/refresh, fell into the catchError below and
  // called logout() — kicking the user back to the login screen seconds after
  // they signed in. Sharing a single in-flight Observable collapses N calls
  // into 1: late subscribers ride along on the same POST and all get the same
  // result.
  private refreshInFlight$: Observable<AuthResponse> | null = null;

  // Observable state
  private readonly currentUserSubject = new BehaviorSubject<User | null>(this.getUserFromStorage());
  public currentUser$ = this.currentUserSubject.asObservable();

  private readonly isAuthenticatedSubject = new BehaviorSubject<boolean>(this.hasValidToken());
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly theme: ThemeService,
  ) {
    // Check and refresh token on initialization if needed
    this.initializeAuth();
    // SCRUM-764: if the user is already authenticated on app boot (e.g. page
    // refresh with a valid token), reconcile the theme with server state.
    if (this.isAuthenticatedSubject.value) {
      this.theme.loadUserPreference().subscribe();
    }
  }

  /**
   * Initialize authentication on service creation
   */
  private initializeAuth(): void {
    if (this.hasValidToken()) {
      this.checkAndRefreshToken();
      // Resume inactivity tracking on page reload while a session is active.
      this.startInactivityTimer();
    }
  }

  /**
   * Register a new user
   */
  register(data: RegisterData): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.API_URL}/register`, {
      displayName: data.displayName,
      email: data.email,
      password: data.password,
    }, { withCredentials: true }).pipe(
      tap((response) => {
        this.storeTokens(response.accessToken, response.expiresIn);
        if (response.user) {
          this.currentUserSubject.next(response.user);
          this.storeUser(response.user);
        }
        this.isAuthenticatedSubject.next(true);
      }),
      catchError((error) => this.handleError(error))
    );
  }

  /**
   * Login user
   */
  login(credentials: AuthCredentials, rememberMe: boolean = false): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.API_URL}/login`, {
      email: credentials.email,
      password: credentials.password,
      // SCRUM-2017 — "Trust this device": request the 90-day trusted-device
      // session from the server instead of the default 7-day window.
      trustDevice: rememberMe,
    }, { withCredentials: true }).pipe(
      tap((response) => {
        this.storeTokens(response.accessToken, response.expiresIn);
        if (response.user) {
          this.currentUserSubject.next(response.user);
          this.storeUser(response.user);
        }
        this.isAuthenticatedSubject.next(true);

        if (rememberMe) {
          localStorage.setItem('hub_remember_me', 'true');
        }
        // SCRUM-2017 — persist the trusted signal the server confirmed so the
        // idle-logout exemption (SCRUM-2018) can read it.
        if ((response as { trusted?: boolean }).trusted) {
          localStorage.setItem('hub_trusted', 'true');
        } else {
          localStorage.removeItem('hub_trusted');
        }

        // SCRUM-764: reconcile theme with the server preference so the
        // user's choice follows them across devices/sessions.
        this.theme.loadUserPreference().subscribe();
      }),
      catchError((error) => this.handleError(error))
    );
  }

  // --- Passkeys (WebAuthn) — SCRUM-1699/1700/1701/1702/1703 ---

  /** Whether this browser supports WebAuthn at all. Drives progressive
   *  enhancement: the passkey button is hidden when this is false, so the
   *  password form is never affected on unsupported browsers. */
  passkeySupported(): boolean {
    return browserSupportsWebAuthn();
  }

  /** Whether conditional UI (passkey autofill in the sign-in field) is available. */
  passkeyAutofillSupported(): Promise<boolean> {
    return browserSupportsWebAuthnAutofill();
  }

  /**
   * SCRUM-1771 — whether a passkey sign-in can actually SUCCEED on this device,
   * not merely whether the WebAuthn API exists. True when the browser supports
   * WebAuthn AND it has either a built-in platform authenticator (Face/Touch-ID
   * style) or a cross-device (phone via QR) hybrid transport. When neither is
   * present — e.g. the Tesla in-car browser — the caller hides the passkey
   * button so users aren't dead-ended at an unusable security-key prompt.
   * Fails safe to `false` on any error; the password path is unaffected.
   */
  async passkeyUsable(): Promise<boolean> {
    try {
      if (!browserSupportsWebAuthn()) return false;
      if (await platformAuthenticatorIsAvailable()) return true;
      return await this.hybridTransportAvailable();
    } catch {
      return false;
    }
  }

  /** Whether the browser reports a cross-device (phone/QR) hybrid transport via
   *  the WebAuthn client-capabilities API. Browsers that lack the API entirely
   *  (e.g. the Tesla in-car browser) yield false, which conservatively hides the
   *  passkey button there rather than offering an unusable flow. */
  private async hybridTransportAvailable(): Promise<boolean> {
    try {
      const pkc = (globalThis as {
        PublicKeyCredential?: { getClientCapabilities?: () => Promise<Record<string, boolean>> };
      }).PublicKeyCredential;
      if (!pkc || typeof pkc.getClientCapabilities !== 'function') return false;
      const caps = await pkc.getClientCapabilities();
      return caps?.['hybridTransport'] === true;
    } catch {
      return false;
    }
  }

  /**
   * Usernameless passkey sign-in. Fetches options, runs the browser ceremony,
   * verifies server-side, and applies the SAME session the password login does.
   * `useAutofill` drives conditional UI — the browser surfaces an available
   * passkey directly in the sign-in field.
   */
  async loginWithPasskey(useAutofill = false, email?: string): Promise<AuthResponse> {
    // SCRUM-2060 — identify-first: pass the typed email so the server can scope
    // allowCredentials and the OS chooser drops the "Security key" branch. When
    // no email is given (autofill / escape hatch) the request stays usernameless.
    const body = email && email.trim() ? { email: email.trim() } : {};
    const { ceremonyId, options } = await firstValueFrom(
      this.http.post<{ ceremonyId: string; options: PublicKeyCredentialRequestOptionsJSON }>(
        `${this.API_URL}/webauthn/login/options`, body, { withCredentials: true },
      ),
    );
    const assertion = await startAuthentication({ optionsJSON: options, useBrowserAutofill: useAutofill });
    // SCRUM-1973 — surface which authenticator satisfied the ceremony so the
    // caller can offer a LOCAL passkey after a cross-device (phone/QR) sign-in.
    // authenticatorAttachment is 'platform' | 'cross-platform' | undefined.
    this._lastPasskeyAttachment = assertion.authenticatorAttachment ?? null;
    try {
      const response = await firstValueFrom(
        this.http.post<AuthResponse>(
          `${this.API_URL}/webauthn/login/verify`, { ceremonyId, response: assertion }, { withCredentials: true },
        ),
      );
      this.applyPasskeySession(response);
      return response;
    } catch (err) {
      // SCRUM-1974 — if the server didn't recognise this credential (it was
      // revoked server-side but still lives on the device), tell the browser to
      // drop it so it stops offering a passkey that can no longer sign in.
      // Best-effort and silent; a network/transient error is NOT treated as
      // "unknown credential".
      if (this.isUnknownCredentialRejection(err)) {
        void this.signalUnknownCredential(assertion.id);
      }
      throw err;
    }
  }

  /** The authenticatorAttachment of the most recent passkey sign-in
   *  ('platform' | 'cross-platform' | null). SCRUM-1973 uses this to decide
   *  whether to offer adding a local passkey after a cross-device login. */
  get lastPasskeyAuthenticatorAttachment(): string | null {
    return this._lastPasskeyAttachment;
  }
  private _lastPasskeyAttachment: string | null = null;

  /** True for a definitive "this credential is not known" rejection from the
   *  passkey verify endpoint (400/401/404) — as opposed to a transient network
   *  (status 0) or server (5xx) error, which must not drop a valid credential. */
  private isUnknownCredentialRejection(err: unknown): boolean {
    const status = (err as { status?: number } | null)?.status;
    return status === 400 || status === 401 || status === 404;
  }

  /**
   * SCRUM-1974 — credential-hygiene signal. Feature-detects
   * PublicKeyCredential.signalUnknownCredential and, where supported, tells the
   * browser that `credentialId` is no longer accepted for this relying party so
   * it removes the stale passkey from the platform authenticator. Silent no-op
   * on browsers without the API and on any error — never breaks the delete/login
   * flow. No backend change: uses the existing origin host as the RP id.
   */
  async signalUnknownCredential(credentialId: string): Promise<void> {
    try {
      if (!credentialId) return;
      const pkc = (globalThis as {
        PublicKeyCredential?: { signalUnknownCredential?: (opts: { rpId: string; credentialId: string }) => Promise<void> };
      }).PublicKeyCredential;
      if (!pkc || typeof pkc.signalUnknownCredential !== 'function') return;
      await pkc.signalUnknownCredential({ rpId: this.webauthnRpId(), credentialId });
    } catch {
      /* unsupported / malformed id / user-agent refusal — best effort */
    }
  }

  /** Whether this browser exposes the WebAuthn credential-hygiene signal API. */
  credentialHygieneSupported(): boolean {
    try {
      const pkc = (globalThis as {
        PublicKeyCredential?: { signalUnknownCredential?: unknown };
      }).PublicKeyCredential;
      return !!pkc && typeof pkc.signalUnknownCredential === 'function';
    } catch {
      return false;
    }
  }

  /** RP id for WebAuthn signal calls. The frontend has no configured rpId — the
   *  server derives it from the request origin — so we use the current origin
   *  host, which is the effective domain the passkeys were created against. */
  private webauthnRpId(): string {
    try {
      return window.location.hostname;
    } catch {
      return '';
    }
  }

  /**
   * Tear down any in-flight passkey ceremony (e.g. the background conditional-UI
   * autofill one armed on the login page). Two concurrent
   * navigator.credentials.get() calls stall Safari on iOS/iPadOS, so an explicit
   * "Sign in with a passkey" tap must cancel the autofill ceremony first.
   */
  cancelPasskeyCeremony(): void {
    WebAuthnAbortService.cancelCeremony();
  }

  /** Register a passkey for the signed-in user (account settings — SCRUM-1701). */
  async registerPasskey(nickname?: string): Promise<{ verified: boolean }> {
    const options = await firstValueFrom(
      this.http.post<PublicKeyCredentialCreationOptionsJSON>(
        `${this.API_URL}/webauthn/register/options`, {}, { withCredentials: true },
      ),
    );
    const attestation = await startRegistration({ optionsJSON: options });
    return firstValueFrom(
      this.http.post<{ verified: boolean }>(
        `${this.API_URL}/webauthn/register/verify`, { response: attestation, nickname }, { withCredentials: true },
      ),
    );
  }

  /** List the user's passkeys (account settings — SCRUM-1701/1703). */
  listPasskeys(): Observable<{ passkeys: PasskeySummary[] }> {
    return this.http.get<{ passkeys: PasskeySummary[] }>(`${this.API_URL}/webauthn/credentials`, { withCredentials: true })
      .pipe(catchError((error) => this.handleError(error)));
  }

  /** Rename a passkey. */
  renamePasskey(id: string, nickname: string): Observable<unknown> {
    return this.http.patch(`${this.API_URL}/webauthn/credentials/${id}`, { nickname }, { withCredentials: true })
      .pipe(catchError((error) => this.handleError(error)));
  }

  /** Revoke a passkey. The password remains, so this never locks the user out. */
  deletePasskey(id: string): Observable<unknown> {
    return this.http.delete(`${this.API_URL}/webauthn/credentials/${id}`, { withCredentials: true })
      .pipe(catchError((error) => this.handleError(error)));
  }

  /** List the user's trusted devices (account settings — SCRUM-2019). */
  listTrustedDevices(): Observable<{ devices: TrustedDeviceSummary[] }> {
    return this.http.get<{ devices: TrustedDeviceSummary[] }>(`${this.API_URL}/trusted-devices`, { withCredentials: true })
      .pipe(catchError((error) => this.handleError(error)));
  }

  /** Revoke a trusted device — the safety valve for a sold/stolen car (SCRUM-2019).
   *  The session dies on its next request; the device drops off the list. */
  revokeTrustedDevice(id: string): Observable<unknown> {
    return this.http.delete(`${this.API_URL}/trusted-devices/${id}`, { withCredentials: true })
      .pipe(catchError((error) => this.handleError(error)));
  }

  /** Apply a passkey login response as a session — mirrors the password login's
   *  token handling so downstream state is identical. */
  private applyPasskeySession(response: AuthResponse): void {
    this.storeTokens(response.accessToken, response.expiresIn);
    if (response.user) {
      this.currentUserSubject.next(response.user);
      this.storeUser(response.user);
    }
    this.isAuthenticatedSubject.next(true);
    this.theme.loadUserPreference().subscribe();
  }

  /**
   * Password reset — request an email with a reset link.
   * The backend always returns the same message regardless of whether
   * the email is registered, to prevent user enumeration.
   */
  forgotPassword(email: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.API_URL}/forgot-password`, { email })
      .pipe(catchError((error) => this.handleError(error)));
  }

  /**
   * Password reset — confirm a token is still valid before showing the form.
   */
  validateResetToken(token: string): Observable<{ valid: boolean; error?: string }> {
    const params = `?token=${encodeURIComponent(token)}`;
    return this.http.get<{ valid: boolean; error?: string }>(`${this.API_URL}/reset-password/validate${params}`)
      .pipe(catchError((error) => this.handleError(error)));
  }

  /**
   * Password reset — consume the token and set the new password.
   */
  resetPassword(token: string, newPassword: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.API_URL}/reset-password`, {
      token,
      new_password: newPassword,
    }).pipe(catchError((error) => this.handleError(error)));
  }

  /**
   * Logout user — also calls /auth/logout with withCredentials so the server
   * can clear the HttpOnly hub_refresh cookie.
   */
  logout(): void {
    this.stopInactivityTimer();
    this.clearTokens();
    this.currentUserSubject.next(null);
    this.isAuthenticatedSubject.next(false);
    localStorage.removeItem('hub_remember_me');
    localStorage.removeItem('hub_trusted'); // SCRUM-2017 — drop the trusted-device signal
    this.http.post(`${this.API_URL}/logout`, {}, { withCredentials: true }).subscribe({ error: () => {} });
    this.router.navigate(['/auth/login']);
  }

  /** First name from the most recent login response. */
  getFirstName(): string {
    return localStorage.getItem(this.FIRST_NAME_KEY) || '';
  }

  /** Display name (firstName + lastName, or username fallback). */
  getDisplayName(): string {
    return localStorage.getItem(this.DISPLAY_NAME_KEY) || '';
  }

  /** Current user's role from the most recent login response. */
  getCurrentRole(): string {
    return localStorage.getItem(this.ROLE_KEY) || 'investor';
  }

  /** Persist the optional fields returned by /auth/login. */
  storeLoginExtras(resp: { firstName?: string; lastName?: string; displayName?: string; username?: string; role?: string }): void {
    localStorage.setItem(this.FIRST_NAME_KEY, resp.firstName || '');
    localStorage.setItem(this.DISPLAY_NAME_KEY, resp.displayName || resp.username || '');
    localStorage.setItem(this.ROLE_KEY, resp.role || 'investor');
  }

  /** Sync the BehaviorSubject from localStorage after a manual login flow. */
  notifyLoginFromStorage(): void {
    const user = this.getUserFromStorage();
    if (user) {
      this.currentUserSubject.next(user);
      this.isAuthenticatedSubject.next(true);
    }
  }

  /**
   * Begin tracking user activity. Idempotent — safe to call after every login.
   */
  startInactivityTimer(): void {
    this.stopInactivityTimer();
    // SCRUM-2018 — a trusted device is exempt from the 30-minute idle
    // auto-logout: a car parked idle in a lot must not be signed out. On a
    // trusted session we skip the inactivity timeout + DOM-activity listeners.
    // The session-validity watchdog below stays on for EVERYONE: it silently
    // refreshes a trusted session (keeping the 90-day window alive) and still
    // ejects it the moment the device is revoked server-side (SCRUM-2017) —
    // so the exemption ends on revocation. Untrusted devices are unchanged.
    if (!this.isTrustedSession()) {
      this.resetInactivityTimer();
      this.ACTIVITY_EVENTS.forEach(event => {
        document.addEventListener(event, this.activityHandler, { passive: true });
      });
    }
    // Session-expiry watchdog: catches token timeout even when the inactivity
    // setTimeout is throttled (backgrounded tab) or suspended (laptop sleep).
    document.addEventListener('visibilitychange', this.visibilityHandler);
    window.addEventListener('focus', this.visibilityHandler);
    this.sessionWatchdog = setInterval(() => this.enforceSessionValidity(), this.SESSION_CHECK_INTERVAL);
  }

  /** SCRUM-2017/2018 — whether this session opted into trusted-device treatment
   *  at login (the `hub_trusted` signal set by the login flow). Drives the
   *  idle-logout exemption. */
  isTrustedSession(): boolean {
    try {
      return localStorage.getItem('hub_trusted') === 'true';
    } catch {
      return false;
    }
  }

  stopInactivityTimer(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
    this.ACTIVITY_EVENTS.forEach(event => {
      document.removeEventListener(event, this.activityHandler);
    });
    document.removeEventListener('visibilitychange', this.visibilityHandler);
    window.removeEventListener('focus', this.visibilityHandler);
    if (this.sessionWatchdog) {
      clearInterval(this.sessionWatchdog);
      this.sessionWatchdog = null;
    }
  }

  private resetInactivityTimer(): void {
    if (this.inactivityTimer) clearTimeout(this.inactivityTimer);
    this.inactivityTimer = setTimeout(() => this.handleInactivityLogout(), this.INACTIVITY_TIMEOUT);
  }

  private handleInactivityLogout(): void {
    this.stopInactivityTimer();
    this.clearTokens();
    this.currentUserSubject.next(null);
    this.isAuthenticatedSubject.next(false);
    localStorage.removeItem('hub_remember_me');
    localStorage.removeItem('hub_trusted'); // SCRUM-2017 — drop the trusted-device signal
    this.router.navigate(['/auth/login'], { queryParams: { reason: 'inactivity' } });
  }

  /** True once the stored access token has passed its expiry timestamp. */
  private isAccessTokenExpired(): boolean {
    const expiresIn = this.getTokenExpiresIn();
    if (!expiresIn) return false; // no expiry recorded — leave the session alone
    const expirationTime = Number.parseInt(expiresIn, 10);
    if (!Number.isFinite(expirationTime)) return false;
    return Date.now() >= expirationTime;
  }

  /**
   * Proactive session-timeout guard. If the access token has expired, attempt a
   * silent refresh (the HttpOnly refresh cookie may still be valid). On success
   * the session continues transparently; on a genuine auth rejection (401/403)
   * refreshToken() logs out and we redirect to login with reason=session_expired
   * so the banner explains why. A transient network/server error is ignored so
   * the session survives a momentary blip. Invoked on tab focus/visibility and on a 60s
   * interval, so an idle or backgrounded session is bounced to login the moment
   * it times out — not only on the next API request.
   */
  private enforceSessionValidity(): void {
    if (!this.getAccessToken()) return;        // no active session to guard
    if (!this.isAccessTokenExpired()) return;  // token still within its window
    if (this.refreshInFlight$) return;         // a refresh is already resolving this
    this.refreshToken().subscribe({
      next: () => { /* renewed silently — session continues */ },
      error: (error) => {
        // Bounce to login only when the refresh token is genuinely rejected.
        // On a transient network/server error we keep the session and let the
        // 60s interval retry — otherwise a momentary connectivity gap on wake
        // would eject a user whose refresh cookie is still valid.
        if (this.isAuthRejection(error)) {
          this.router.navigate(['/auth/login'], { queryParams: { reason: 'session_expired' } });
        }
      },
    });
  }

  /** True for a definitive rejection of the refresh — the session cannot be
   *  renewed and the user must re-authenticate. /auth/refresh returns 400 when
   *  the refresh cookie is absent ("Refresh token is required" — e.g. after the
   *  7-day cookie lapses) and 401/403 when the token is invalid/revoked/expired.
   *  A transient network (status 0) or server (5xx) failure is NOT a rejection,
   *  so a momentary blip doesn't eject a still-valid session. */
  private isAuthRejection(error: unknown): boolean {
    const status = (error as { status?: number } | null)?.status;
    return status === 400 || status === 401 || status === 403;
  }

  /**
   * Refresh the access token.
   * The refresh token travels as an HttpOnly cookie (hub_refresh) so no body
   * is needed; withCredentials ensures the browser includes it.
   */
  refreshToken(): Observable<AuthResponse> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }
    this.refreshInFlight$ = this.http.post<AuthResponse>(`${this.API_URL}/refresh`, {}, { withCredentials: true }).pipe(
      tap((response) => {
        this.storeTokens(response.accessToken, response.expiresIn);
        if (response.user) {
          this.currentUserSubject.next(response.user);
          this.storeUser(response.user);
        }
      }),
      catchError((error) => {
        // Only a genuine auth rejection (401/403) means the refresh token is
        // dead — then we log out. A network error (status 0) or a transient
        // server error (5xx), common right after a laptop wakes while the
        // network is still reconnecting, must NOT nuke a valid session; let the
        // caller retry on its next interval.
        if (this.isAuthRejection(error)) {
          this.logout();
        }
        return throwError(() => error);
      }),
      finalize(() => {
        this.refreshInFlight$ = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    return this.refreshInFlight$;
  }

  /**
   * Check if token will expire soon and refresh if needed
   */
  checkAndRefreshToken(): void {
    const expiresIn = this.getTokenExpiresIn();
    if (!expiresIn) return;

    const expirationTime = Number.parseInt(expiresIn, 10);
    const currentTime = Date.now();

    // Refresh if token expires in less than 5 minutes
    const refreshThreshold = 5 * 60 * 1000;
    if (expirationTime - currentTime < refreshThreshold && expirationTime > currentTime) {
      this.refreshToken().subscribe({
        error: (error) => {
          // Only a real auth rejection logs the user out; a transient
          // network/server error leaves the session intact to retry later.
          if (this.isAuthRejection(error)) {
            this.logout();
          }
        },
      });
    }
  }

  /**
   * Get current access token
   */
  getAccessToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Get current user
   */
  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.hasValidToken();
  }

  /**
   * Check if there is a valid token in storage
   */
  private hasValidToken(): boolean {
    const token = this.getAccessToken();
    return !!token;
  }

  /**
   * Store tokens in localStorage. Only the short-lived access token is
   * stored here; the refresh token lives in the HttpOnly hub_refresh cookie
   * set by the server and is never accessible to JavaScript.
   */
  private storeTokens(accessToken: string, expiresIn: number): void {
    localStorage.setItem(this.TOKEN_KEY, accessToken);
    const expirationTime = Date.now() + expiresIn * 1000;
    localStorage.setItem(this.EXPIRES_IN_KEY, expirationTime.toString());
  }

  /**
   * Store user in localStorage
   */
  private storeUser(user: User): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  /**
   * Get user from localStorage
   */
  private getUserFromStorage(): User | null {
    const user = localStorage.getItem(this.USER_KEY);
    return user ? JSON.parse(user) : null;
  }

  /**
   * Get token expiration time
   */
  private getTokenExpiresIn(): string | null {
    return localStorage.getItem(this.EXPIRES_IN_KEY);
  }

  /**
   * Clear tokens from localStorage
   */
  private clearTokens(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.EXPIRES_IN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.FIRST_NAME_KEY);
    localStorage.removeItem(this.DISPLAY_NAME_KEY);
    localStorage.removeItem(this.ROLE_KEY);
  }

  /**
   * Handle HTTP errors
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An unknown error occurred';

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = error.error.message;
    } else {
      // Server-side error
      errorMessage = error.error?.message || error.statusText || errorMessage; // NOSONAR S1874 — statusText is deprecated in Angular 21 but still populated by the browser for error display
    }

    console.error('[AuthService] Error:', errorMessage);
    return throwError(() => new Error(errorMessage));
  }
}
